import React, { useCallback, useRef } from 'react'
import { cn } from './cn'

export interface ViewTab {
  /** Stable id — also derives the `aria-controls` panel id. */
  id: string
  label: React.ReactNode
  /**
   * Optional count badge. ZERO IS NOT RENDERED: "0" is not information, and a
   * badge showing it reads as a broken counter rather than an empty list.
   */
  count?: number
}

export interface ViewTabsProps {
  tabs: ViewTab[]
  activeId: string
  onChange: (id: string) => void
  /** Required: a tablist with no name is unusable in a screen reader. */
  label: string
  /**
   * Optional trailing action (e.g.「＋ 換個範本再整理」).
   *
   * Rendered OUTSIDE the `role="tablist"` element on purpose. A tablist may
   * only contain `role="tab"` children; a button smuggled in there gets counted
   * as an extra tab, so a screen reader announces "3 of 3" for a two-tab list
   * and arrow-key navigation lands on something that isn't a view.
   */
  action?: React.ReactNode
  className?: string
}

/**
 * 檢視 tab — mutually exclusive views of ONE set of data.
 *
 * The single implementation behind every tab row in the product: 逐字稿／會議記錄,
 * 我的範本／內建範本. It exists because those started as separate copies and
 * drifted — a type-scale change landed in two of them and left the others at the
 * old size. The panel also owns the first content gap, so every consumer gets
 * the same breathing room below the tab underline without retaining stale
 * page-local `.ptabs` spacing.
 *
 * `role="tab"` is a keyboard contract, not decoration, so this honours it:
 * roving tabindex (exactly one tab is in the tab order) plus ArrowLeft/Right to
 * move between them. Geometry is expressed through the shared token utilities.
 */
export function ViewTabs({ tabs, activeId, onChange, label, action, className }: ViewTabsProps) {
  const listRef = useRef<HTMLDivElement>(null)

  const move = useCallback(
    (from: number, delta: number) => {
      const next = (from + delta + tabs.length) % tabs.length
      onChange(tabs[next].id)
      // Focus follows selection: with a roving tabindex the previously-focused
      // tab has just left the tab order, so without this the caret is nowhere.
      const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      buttons?.[next]?.focus()
    },
    [tabs, onChange]
  )

  return (
    <div className={cn('flex items-end flex-wrap border-b border-border', className)}>
      <div
        className="flex flex-wrap items-end gap-[var(--gap-xs)]"
        role="tablist"
        aria-label={label}
        ref={listRef}
      >
        {tabs.map((tab, index) => {
          const selected = tab.id === activeId
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-controls={`panel-${tab.id}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className={cn(
                'inline-flex items-center gap-1.5 -mb-px border-b-2 border-transparent px-3 pb-2.5 text-sm text-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)]',
                selected && 'border-foreground font-semibold text-foreground'
              )}
              onClick={() => onChange(tab.id)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') {
                  event.preventDefault()
                  move(index, 1)
                } else if (event.key === 'ArrowLeft') {
                  event.preventDefault()
                  move(index, -1)
                }
              }}
            >
              {tab.label}
              {tab.count ? (
                <span
                  className={cn(
                    'rounded-full border border-border px-1.5 font-mono text-2xs leading-4 text-muted',
                    selected && 'border-foreground text-foreground'
                  )}
                >
                  {tab.count}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
      {action}
    </div>
  )
}

export interface ViewTabPanelProps {
  /** Must match the `ViewTab.id` this panel belongs to. */
  id: string
  active: boolean
  children: React.ReactNode
  className?: string
}

/** The panel half of the contract — wired to its tab by id. */
export function ViewTabPanel({ id, active, children, className }: ViewTabPanelProps) {
  return (
    <div
      id={`panel-${id}`}
      role="tabpanel"
      aria-labelledby={`tab-${id}`}
      hidden={!active}
      className={cn('mt-[var(--gap-md)]', className)}
    >
      {children}
    </div>
  )
}
